import {
  attachAssessmentRecords,
  buildSystemAnalyses,
  getAliasedValue,
  normalizeCriterionId,
  type SystemAnalysisDataset,
} from "@/domain/systemAnalysis";
import type { SourceRow } from "@/domain/types";
import { getSupabase, unwrap } from "./supabaseClient";

const PAGE_SIZE = 500;

interface AssessmentRecord {
  row_data: SourceRow;
  year: string | number;
  criterion_id: string;
  location: string;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Reads every row of a table in worksheet order, past Supabase's default row
 * limit, optionally limited to one `location`.
 */
async function fetchAllRows<T>(
  table: string,
  columns: string,
  location: string | null = null,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    let query = getSupabase().from(table).select(columns);
    if (location !== null) query = query.eq("location", location);
    const page = unwrap(
      await query.order("source_row").range(offset, offset + PAGE_SIZE - 1),
    ) as T[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

export async function fetchSystemAnalyses(): Promise<SystemAnalysisDataset> {
  const [criteria, assessments] = await Promise.all([
    fetchAllRows<{ row_data: SourceRow }>("system_analysis_criteria", "row_data"),
    fetchAllRows<AssessmentRecord>(
      "system_analysis_assessments",
      "row_data, year, criterion_id, location, updated_at, updated_by",
    ),
  ]);
  const dataset = buildSystemAnalyses(
    criteria.map((record) => record.row_data),
    assessments.map((record) => record.row_data),
  );
  return attachAssessmentRecords(
    dataset,
    assessments.map((record) => ({
      row: record.row_data,
      year: String(record.year),
      criterionId: record.criterion_id,
      location: record.location,
      updatedAt: record.updated_at ?? null,
      updatedBy: record.updated_by ?? null,
    })),
  );
}

/** Annex4_Criterion rows in worksheet order (shared by all locations). */
export async function fetchCriteriaForExport(): Promise<SourceRow[]> {
  const rows = await fetchAllRows<{ row_data: SourceRow }>("system_analysis_criteria", "row_data");
  return rows.map((record) => record.row_data);
}

/** Annex4_Data rows; RLS limits local roles to their own location. */
export async function fetchAssessmentsForExport(location: string | null): Promise<SourceRow[]> {
  const rows = await fetchAllRows<{ row_data: SourceRow }>(
    "system_analysis_assessments",
    "row_data",
    location,
  );
  return rows.map((record) => record.row_data);
}

export interface AssessmentInput {
  criterion_id: string;
  row_data: SourceRow;
}

/**
 * Replaces one location's assessments for one year (RPC checks the caller's
 * role/location). `criteriaYear` is copied to `year` first when that year has
 * no criteria of its own.
 */
export async function saveAssessments(
  year: string,
  location: string,
  rows: AssessmentInput[],
  criteriaYear: string | null = null,
): Promise<void> {
  unwrap(
    await getSupabase().rpc("save_system_analysis_assessments", {
      p_target_year: year,
      p_target_location: location,
      p_assessment_rows: rows,
      p_criteria_year: criteriaYear,
    }),
  );
}

/** Row shape expected by the `import_system_analysis` RPC. */
export interface SystemAnalysisImport {
  criterion_rows: {
    source_row: number;
    year: string;
    criterion_id: string | null;
    area_id: string | null;
    row_data: SourceRow;
  }[];
  data_rows: {
    source_row: number;
    year: string;
    criterion_id: string | null;
    location: string | null;
    row_data: SourceRow;
  }[];
}

export function mapSystemAnalysisRows(
  criterionRows: SourceRow[],
  dataRows: SourceRow[],
): SystemAnalysisImport {
  const base = (row: SourceRow, index: number) => ({
    source_row: index + 1,
    year: getAliasedValue(row, ["Year", "Ar", "År"]),
    criterion_id:
      normalizeCriterionId(
        getAliasedValue(row, ["Criterion_ID", "Criterion ID", "CriterionId", "ID"]),
      ) || null,
    row_data: row,
  });
  return {
    criterion_rows: criterionRows.map((row, index) => ({
      ...base(row, index),
      area_id:
        getAliasedValue(row, ["Area_ID", "Area ID", "Area", "Domain", "Domain_ID"]).toUpperCase() ||
        null,
    })),
    data_rows: dataRows.map((row, index) => ({
      ...base(row, index),
      location: getAliasedValue(row, ["Location", "Region", "Lokasjon", "Sted"]) || null,
    })),
  };
}

export async function importSystemAnalysis(rows: SystemAnalysisImport): Promise<void> {
  unwrap(await getSupabase().rpc("import_system_analysis", rows));
}
